import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Descriptions, Drawer, Empty, Modal, Select, Space, Spin, Table, Tabs, Tag } from "antd";
import MobileCardList from "@components/MobileCardList";
import { useSelector } from "react-redux";
import moment from "moment";
import { api } from "@/redux/api";
import { buildFacilityPath, getFacilityContext } from "@/utils/route-context";
import { formatContractRef } from "@/utils/entity-ref";
import {
  CONTRACT_STATUS_COLORS,
  getDisplayStatus,
  getDisplayStatusLabel,
} from "./constants";
import {
  buildCertificateColumns,
  CERTIFICATE_TABLE_SCROLL_X,
  CERTIFICATE_TAB_TITLE,
  certificateRowKey,
} from "./certificateColumns";

// Mirror the project's H5 detection used by DetailTabs / Responsive components.
function useIsH5(reduxIsPC) {
  const getViewportIsH5 = () =>
    typeof window !== "undefined" ? window.innerWidth <= 767 : reduxIsPC === false;
  const [isH5, setIsH5] = useState(() => reduxIsPC === false || getViewportIsH5());
  useEffect(() => {
    const onResize = () => setIsH5(reduxIsPC === false || getViewportIsH5());
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [reduxIsPC]);
  return isH5;
}

export const getInitials = (value) => {
  const parts = String(value || "?").trim().split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : parts[0] || "?")
    .slice(0, 2)
    .toUpperCase();
};

const getRelationshipRows = (response) => {
  const rows = response?.data?.results || response?.data || response || [];
  return Array.isArray(rows) ? rows : [];
};

// `other_service` is persisted as a comma-separated string; accept either that
// or an already-split array (as returned by some endpoints).
const splitOtherService = (value) => {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
  return String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
};

// A value that has no content renders as a muted dash so the Overview table
// keeps a stable row height.
const renderValue = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") {
    return <span style={{ color: "#8c8c8c" }}>{fallback}</span>;
  }
  return value;
};

// An <a> that survives the modal's default click handling.
function ExternalLink({ href, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => event.stopPropagation()}
    >
      {children}
    </a>
  );
}

// A standalone presenter that opens when the user clicks a contractor / contract
// company name. It renders Overview (full company profile), Certificates,
// Contact and Contract Requests so every page that shows a contractor can share
// one implementation.
//
// Props:
//   open       - whether the modal/drawer is visible
//   onClose    - callback to close it
//   contractor - descriptor of the clicked contractor company:
//                { id, name, email, logo } (minimum: id). `id` is the ContractCompany pk.
//   detail       - optional preloaded relationship-shaped record
//                { contract_company, contract_company_name, contract_company_logo,
//                  contract_company_email, contract_company_address,
//                  contract_company_description, contract_company_other_service,
//                  contract_company_contact_name, contract_company_contact_phone,
//                  contract_company_contact_email, service_categories, certificates,
//                  status, created_date, modified_date }.
//                When provided with service_categories the component uses it directly and
//                does not re-fetch the facility relationship list.
//   companyId / facilityId - optional overrides for the facility context (defaults to redux context).
export default function ContractCompanyDetailModal({
  open,
  onClose,
  contractor,
  detail: initialDetail,
  companyId,
  facilityId,
}) {
  const reduxIsPC = useSelector((state) => state.App?.isPC ?? true);
  const reduxContext = useSelector((state) => getFacilityContext(state));
  const isPC = reduxIsPC;
  const isH5 = useIsH5(isPC);

  const context = useMemo(
    () => ({
      companyId: companyId ?? reduxContext.companyId,
      facilityId: facilityId ?? reduxContext.facilityId,
    }),
    [companyId, facilityId, reduxContext.companyId, reduxContext.facilityId]
  );

  const contractorId = contractor?.id;
  const [relationship, setRelationship] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [h5ActiveKey, setH5ActiveKey] = useState("overview");

  // Merge whatever the caller handed over with the optional relationship record.
  const resolvedDetail = useMemo(() => {
    if (initialDetail) return initialDetail;
    if (relationship) return relationship;
    return null;
  }, [initialDetail, relationship]);

  // Reset the H5 section whenever a different contractor is opened.
  useEffect(() => {
    setH5ActiveKey("overview");
  }, [open, contractorId]);

  // Overview / Certificates / Contact: reuse the caller-provided relationship
  // detail when available; otherwise look the company up in the facility
  // relationship list so the full company profile can be shown consistently.
  useEffect(() => {
    setRelationship(null);
    if (!open || !contractorId || !context.companyId) return;
    // The caller already supplied full relationship data — nothing to look up.
    if (initialDetail?.service_categories !== undefined) return;

    let active = true;
    api
      .getFacilityContractRelationships(context.companyId)
      .then((response) => {
        if (!active) return;
        const rows = getRelationshipRows(response);
        const match = rows.find(
          (row) => String(row.contract_company ?? row.contract_company_id) === String(contractorId)
        );
        setRelationship(match || null);
      })
      .catch(() => {
        if (active) setRelationship(null);
      });
    return () => {
      active = false;
    };
  }, [open, contractorId, context.companyId, initialDetail?.service_categories]);

  const displayName =
    resolvedDetail?.contract_company_name ||
    resolvedDetail?.name ||
    contractor?.name ||
    (contractorId != null ? `Contractor #${contractorId}` : "");
  const displayLogo =
    resolvedDetail?.contract_company_logo ||
    resolvedDetail?.logo ||
    contractor?.logo ||
    null;
  const displayEmail =
    resolvedDetail?.contract_company_email ||
    resolvedDetail?.email ||
    contractor?.email ||
    null;

  // Company profile fields (address / description / services / contacts).
  const displayAddress =
    resolvedDetail?.contract_company_address || resolvedDetail?.address || null;
  const displayDescription =
    resolvedDetail?.contract_company_description || resolvedDetail?.description || null;
  const displayOtherService =
    resolvedDetail?.contract_company_other_service ?? resolvedDetail?.other_service ?? null;
  const displayContactName =
    resolvedDetail?.contract_company_contact_name || resolvedDetail?.contact_name || null;
  const displayContactPhone =
    resolvedDetail?.contract_company_contact_phone || resolvedDetail?.contact_phone || null;
  const displayContactEmail =
    resolvedDetail?.contract_company_contact_email || resolvedDetail?.contact_email || null;

  // Load every contract request this contractor company received whenever the
  // company id + facility are known.
  useEffect(() => {
    if (!open || contractorId == null || !context.facilityId) {
      setRequests([]);
      return;
    }
    let active = true;
    setLoadingRequests(true);
    api
      .getContracts([context.facilityId], {
        contract_company: contractorId,
        page_size: 200,
      })
      .then((response) => {
        if (!active) return;
        const payload = response?.data?.results || response?.data || [];
        setRequests(Array.isArray(payload) ? payload : []);
      })
      .catch(() => {
        if (active) setRequests([]);
      })
      .finally(() => {
        if (active) setLoadingRequests(false);
      });
    return () => {
      active = false;
    };
  }, [open, contractorId, context.facilityId]);

  // Sort contract requests the same way the Contractor Management table does.
  const sortedRequests = useMemo(() => {
    const STATUS_SORT_RANK = {
      sent: 0,
      bidding: 1,
      awarded: 2,
      accepted: 3,
      pending_acceptance: 4,
      not_awarded: 5,
      closed: 6,
      cancelled: 7,
    };
    return [...(requests || [])].sort((a, b) => {
      const rankDelta =
        (STATUS_SORT_RANK[getDisplayStatus(a)] ?? 99) -
        (STATUS_SORT_RANK[getDisplayStatus(b)] ?? 99);
      if (rankDelta) return rankDelta;
      return new Date(b.created_date || 0) - new Date(a.created_date || 0);
    });
  }, [requests]);

  const requestColumns = useMemo(
    () => [
      {
        title: "Title",
        dataIndex: "title",
        key: "title",
        ellipsis: true,
        render: (value, item) => {
          const requestPath = buildFacilityPath(
            `contract-request-detail/${item.id}`,
            { companyId: context.companyId, facilityId: context.facilityId }
          );
          return (
            <a
              href={requestPath || "#"}
              onClick={(event) => {
                event.preventDefault();
                if (requestPath) window.location.href = requestPath;
              }}
            >
              {value || formatContractRef(item.id)}
            </a>
          );
        },
      },
      {
        title: "Status",
        key: "status",
        width: 150,
        render: (_, item) => (
          <Tag color={CONTRACT_STATUS_COLORS[getDisplayStatus(item)] || "default"}>
            {getDisplayStatusLabel(item)}
          </Tag>
        ),
      },
      {
        title: "Due",
        dataIndex: "due_date",
        key: "due_date",
        width: 120,
        render: (value) => (value ? moment(value).format("YYYY-MM-DD") : "-"),
      },
      {
        title: "Created",
        dataIndex: "created_date",
        key: "created_date",
        width: 150,
        render: (value) => (value ? moment(value).format("YYYY-MM-DD HH:mm") : "-"),
      },
    ],
    [context.companyId, context.facilityId]
  );

  // Main service = the company's service categories.
  const serviceCategories = useMemo(() => {
    const source =
      resolvedDetail?.service_categories ||
      resolvedDetail?.service_category_details ||
      resolvedDetail?.contract_company_service_categories;
    if (Array.isArray(source)) return source.filter((item) => item && item.name);
    return [];
  }, [resolvedDetail]);

  // Other service = free-text tags stored on the company.
  const otherServices = useMemo(() => splitOtherService(displayOtherService), [displayOtherService]);

  // Certificates owned by the company.
  const certificates = useMemo(() => {
    const source =
      resolvedDetail?.certificates || resolvedDetail?.contract_company_certificates;
    if (Array.isArray(source)) return source.filter(Boolean);
    return [];
  }, [resolvedDetail]);

  const certificateColumns = useMemo(() => buildCertificateColumns(), []);

  const sections = useMemo(() => {
    const list = [
      {
        key: "overview",
        label: "Overview",
        render: () => (
          <Descriptions column={1} size="middle" bordered>
            <Descriptions.Item label="Name">
              <Space>
                <Avatar src={displayLogo}>{getInitials(displayName)}</Avatar>
                {displayName || "-"}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Login email">
              {renderValue(displayEmail)}
            </Descriptions.Item>
            <Descriptions.Item label="Address">{renderValue(displayAddress)}</Descriptions.Item>
            <Descriptions.Item label="Description">
              {renderValue(displayDescription)}
            </Descriptions.Item>
            <Descriptions.Item label="Main service">
              {serviceCategories.length ? (
                <Space wrap size={[8, 10]}>
                  {serviceCategories.map((item) => (
                    <Tag key={item.id || item.name} color="blue">
                      {item.name}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <span style={{ color: "#8c8c8c" }}>No main service</span>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Other service">
              {otherServices.length ? (
                <Space wrap size={[8, 10]}>
                  {otherServices.map((item) => (
                    <Tag key={item}>{item}</Tag>
                  ))}
                </Space>
              ) : (
                <span style={{ color: "#8c8c8c" }}>No other service</span>
              )}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: "certificates",
        label: CERTIFICATE_TAB_TITLE(certificates.length),
        render: () =>
          certificates.length ? (
            isH5 ? (
              <MobileCardList
                size="small"
                rowKey={certificateRowKey}
                columns={certificateColumns}
                dataSource={certificates}
                pagination={false}
                locale={{ emptyText: "No licenses or certificates" }}
              />
            ) : (
              <Table
                size="small"
                rowKey={certificateRowKey}
                dataSource={certificates}
                scroll={{ x: CERTIFICATE_TABLE_SCROLL_X }}
                pagination={false}
                locale={{ emptyText: "No licenses or certificates" }}
                columns={certificateColumns}
              />
            )
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No licenses or certificates"
              style={{ marginTop: 24 }}
            />
          ),
      },
      {
        key: "contact",
        label: "Contact",
        render: () => (
          <Descriptions column={1} size="middle" bordered>
            <Descriptions.Item label="Contact name">
              {renderValue(displayContactName)}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              {renderValue(displayContactPhone)}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {displayContactEmail ? (
                <ExternalLink href={`mailto:${displayContactEmail}`}>
                  {displayContactEmail}
                </ExternalLink>
              ) : (
                <span style={{ color: "#8c8c8c" }}>-</span>
              )}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
    ];
    if (contractorId != null) {
      list.push({
        key: "contract-requests",
        label: `Contract Requests${sortedRequests.length ? ` (${sortedRequests.length})` : ""}`,
        render: () => (
          <Spin spinning={loadingRequests}>
            {isH5 ? (
              <MobileCardList
                size="small"
                rowKey="id"
                columns={requestColumns}
                dataSource={sortedRequests}
                loading={loadingRequests}
                pagination={{ defaultPageSize: 20 }}
                locale={{ emptyText: "No contract requests" }}
              />
            ) : (
              <Table
                size="small"
                rowKey="id"
                dataSource={sortedRequests}
                scroll={{ x: 700 }}
                pagination={{
                  defaultPageSize: 20,
                  showSizeChanger: true,
                  pageSizeOptions: ["20", "50", "100"],
                  showTotal: (total) => `${total} requests`,
                }}
                locale={{ emptyText: "No contract requests" }}
                columns={requestColumns}
              />
            )}
          </Spin>
        ),
      });
    }
    return list;
  }, [
    certificateColumns,
    certificates,
    contractorId,
    displayAddress,
    displayContactEmail,
    displayContactName,
    displayContactPhone,
    displayDescription,
    displayEmail,
    displayLogo,
    displayName,
    isH5,
    loadingRequests,
    otherServices,
    requestColumns,
    serviceCategories,
    sortedRequests,
  ]);

  const renderSection = (key) => sections.find((section) => section.key === key)?.render?.();

  const content =
    open && sections.length ? (
      isH5 ? (
        <div>
          {sections.length > 1 ? (
            <Select
              style={{ width: "100%", marginBottom: 16 }}
              value={h5ActiveKey}
              onChange={setH5ActiveKey}
              options={sections.map((section) => ({ value: section.key, label: section.label }))}
            />
          ) : null}
          {renderSection(h5ActiveKey)}
        </div>
      ) : (
        <Tabs defaultActiveKey="overview" destroyInactiveTabPane>
          {sections.map((section) => (
            <Tabs.TabPane tab={section.label} key={section.key}>
              {section.render()}
            </Tabs.TabPane>
          ))}
        </Tabs>
      )
    ) : null;

  const title = displayName || "Contractor Details";

  if (isPC) {
    return (
      <Modal
        open={open}
        title={title}
        onCancel={onClose}
        footer={null}
        width="80%"
        bodyStyle={{ paddingTop: 8 }}
      >
        {content}
      </Modal>
    );
  }

  return (
    <Drawer
      placement="bottom"
      height="100vh"
      visible={open}
      title={title}
      onClose={onClose}
      destroyOnClose={false}
      className="contract-relationship-detail-drawer"
      bodyStyle={{ paddingTop: 12, overflowY: "auto" }}
    >
      {content}
    </Drawer>
  );
}
